import { FetchService } from '@affine/core/modules/cloud';
import {
  type WorkspaceFlavourProvider,
  WorkspaceService,
  WorkspacesService,
} from '@affine/core/modules/workspace';
import { WorkspaceImpl } from '@affine/core/modules/workspace/impls/workspace';
import { DebugLogger } from '@affine/debug';
import type { ListHistoryQuery } from '@affine/graphql';
import { listHistoryQuery } from '@affine/graphql';
import { i18nTime } from '@affine/i18n';
import type { Workspace } from '@blocksuite/affine/store';
import type {
  RecoveryCandidateTimeline,
  RecoveryRunResult,
} from '@streamlain/doc-sync-taxonomy';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWRImmutable from 'swr/immutable';
import {
  applyUpdate,
  Doc as YDoc,
  encodeStateAsUpdate,
  encodeStateVector,
  UndoManager,
} from 'yjs';

import { useMutateQueryResource } from '../../../components/hooks/use-mutation';
import { useQueryInfinite } from '../../../components/hooks/use-query';
import { resolveHistoryPreviewState } from './preview-state';

const logger = new DebugLogger('page-history');

type DocHistory = ListHistoryQuery['workspace']['histories'][number];

export type EncodedHistorySnapshotBundle = {
  page: {
    docId: string;
    snapshot: string;
  };
  systemDocs: Array<{
    docId: string;
    snapshot: string;
  }>;
  warnings?: string[];
};

export type DecodedHistorySnapshotBundle = {
  page: {
    docId: string;
    snapshot: ArrayBuffer;
  };
  systemDocs: Array<{
    docId: string;
    snapshot: ArrayBuffer;
  }>;
  warnings: string[];
};

type RecoveryCandidateEntry = RecoveryCandidateTimeline['entries'][number];

export type DocHistoryCalendarDay = {
  date: string;
  count: number;
  firstTimestamp: string;
  latestTimestamp: string;
};

export type DocHistoryCalendar = {
  docId: string;
  start: string;
  end: string;
  timezone: string;
  days: DocHistoryCalendarDay[];
  totalEntries: number;
};

const RECOVERY_CANDIDATE_FINGERPRINT_MISMATCH_CODE =
  'recovery_candidate_fingerprint_mismatch';
const RECOVERY_VERSION_KEY = (workspaceId: string) =>
  `sl_recovery_version_${workspaceId}`;

class RecoveryApiError extends Error {
  readonly code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'RecoveryApiError';
    this.code = code;
  }
}

const getRecoveryApiErrorCode = (error: unknown): string | undefined => {
  return typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as { code?: unknown }).code === 'string'
    ? (error as { code: string }).code
    : undefined;
};

export const useDocSnapshotList = (
  workspaceId: string,
  pageDocId: string,
  beforeTimestamp?: string,
  pageSize = 10,
  singlePage = false
) => {
  const { data, loadingMore, loadMore } = useQueryInfinite({
    query: listHistoryQuery,
    getVariables: (pageIndex, previousPageData) => {
      // use the timestamp of the last history as the cursor
      const before =
        pageIndex === 0
          ? beforeTimestamp
          : previousPageData?.workspace.histories.at(-1)?.timestamp;
      const vars = {
        pageDocId: pageDocId,
        workspaceId: workspaceId,
        before: before,
        take: pageSize,
      };

      return vars;
    },
  });

  const shouldLoadMore = useMemo(() => {
    if (!data) {
      return false;
    }
    const lastPage = data.at(-1);
    if (!lastPage) {
      return false;
    }
    return lastPage.workspace.histories.length === pageSize;
  }, [data, pageSize]);

  const histories = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.flatMap(page => page.workspace.histories);
  }, [data]);

  return [
    histories,
    !singlePage && shouldLoadMore ? loadMore : false,
    !!loadingMore,
  ] as const;
};

const snapshotFetcher = async (
  [fetchService, workspaceId, pageDocId, ts]: [
    FetchService,
    workspaceId: string,
    pageDocId: string,
    ts: string,
  ] // timestamp is the key to the history/snapshot
) => {
  if (!ts) {
    return null;
  }
  const res = await fetchService.fetch(
    `/api/workspaces/${workspaceId}/docs/${pageDocId}/histories/${ts}`
  );

  if (!res.ok) {
    throw new Error('Failed to fetch snapshot');
  }

  const snapshot = await res.arrayBuffer();
  if (!snapshot) {
    throw new Error('Invalid snapshot');
  }
  return snapshot;
};

const decodeBase64Snapshot = (value: string): ArrayBuffer => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

export const decodeHistorySnapshotBundle = (
  bundle: EncodedHistorySnapshotBundle
): DecodedHistorySnapshotBundle => ({
  page: {
    docId: bundle.page.docId,
    snapshot: decodeBase64Snapshot(bundle.page.snapshot),
  },
  systemDocs: bundle.systemDocs.map(entry => ({
    docId: entry.docId,
    snapshot: decodeBase64Snapshot(entry.snapshot),
  })),
  warnings: bundle.warnings ?? [],
});

const snapshotBundleFetcher = async ([
  fetchService,
  workspaceId,
  pageDocId,
  ts,
]: [
  FetchService,
  workspaceId: string,
  pageDocId: string,
  ts: string,
]): Promise<{
  timestamp: string;
  bundle: DecodedHistorySnapshotBundle;
} | null> => {
  if (!ts) {
    return null;
  }
  const res = await fetchService.fetch(
    `/api/workspaces/${workspaceId}/docs/${pageDocId}/histories/${ts}/bundle`
  );

  if (!res.ok) {
    throw new Error('Failed to fetch history snapshot bundle');
  }

  const bundle = (await res.json()) as EncodedHistorySnapshotBundle;
  return {
    timestamp: ts,
    bundle: decodeHistorySnapshotBundle(bundle),
  };
};

const historyCalendarFetcher = async ([
  fetchService,
  workspaceId,
  pageDocId,
  start,
  end,
  timezone,
]: [
  FetchService,
  workspaceId: string,
  pageDocId: string,
  start: string,
  end: string,
  timezone: string,
]): Promise<DocHistoryCalendar> => {
  const params = new URLSearchParams({
    start,
    end,
    timezone,
  });
  const response = await fetchService.fetch(
    `/api/workspaces/${workspaceId}/docs/${pageDocId}/history-calendar?${params}`
  );
  return readJsonResponse<DocHistoryCalendar>(
    response,
    'Failed to load history calendar'
  );
};

const readJsonResponse = async <T>(
  response: Response,
  fallbackMessage: string
): Promise<T> => {
  const payload = (await response.json().catch(() => null)) as {
    code?: unknown;
    message?: unknown;
    error?: unknown;
  } | null;

  if (!response.ok) {
    const message =
      typeof payload?.message === 'string'
        ? payload.message
        : typeof payload?.error === 'string'
          ? payload.error
          : fallbackMessage;
    throw new RecoveryApiError(
      message,
      typeof payload?.code === 'string' ? payload.code : undefined
    );
  }

  return payload as T;
};

const findRecoveryCandidateForTimestamp = async (
  fetchService: FetchService,
  workspaceId: string,
  pageDocId: string,
  timestamp: string
): Promise<RecoveryCandidateEntry> => {
  const params = new URLSearchParams({
    limit: '1',
    mode: 'full',
    timestamp,
  });

  const response = await fetchService.fetch(
    `/api/workspaces/${workspaceId}/docs/${pageDocId}/recovery-candidates?${params}`
  );
  const timeline = await readJsonResponse<RecoveryCandidateTimeline>(
    response,
    'Failed to load recovery candidate'
  );
  const candidate = timeline.entries.find(
    entry => entry.timestamp === timestamp
  );
  if (candidate) {
    return candidate;
  }

  throw new Error('Recovery candidate is no longer available');
};

const restoreRecoveryCandidate = async (
  fetchService: FetchService,
  workspaceId: string,
  pageDocId: string,
  candidate: RecoveryCandidateEntry
): Promise<RecoveryRunResult> => {
  if (!candidate.isRestoreable) {
    throw new Error(
      candidate.disabledReasonCode ?? 'Recovery candidate is not restoreable'
    );
  }

  const response = await fetchService.fetch(
    `/api/workspaces/${workspaceId}/docs/${pageDocId}/recoveries`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        timestamp: candidate.timestamp,
        candidateId: candidate.candidateId,
        dataFingerprint: candidate.dataFingerprint,
      }),
    }
  );

  return readJsonResponse<RecoveryRunResult>(
    response,
    'Failed to restore document'
  );
};

const downloadCurrentStateSnapshot = (result: RecoveryRunResult) => {
  const download = result.currentStateSnapshotDownload;
  if (!download) {
    return;
  }

  const link = document.createElement('a');
  link.href = `data:${download.mimeType};base64,${download.base64}`;
  link.download = download.fileName;
  document.body.append(link);
  link.click();
  link.remove();
};

const resetRecoveredLocalDocs = async (
  workspaceService: WorkspaceService,
  docCollection: Workspace,
  recoveredDocIds: readonly string[]
): Promise<void> => {
  const docFrontend = workspaceService.workspace.engine.doc;
  const resetLocalDoc = docFrontend.storage.resetLocalDoc;
  if (!resetLocalDoc) {
    throw new Error('Local doc reset is not available');
  }

  for (const docId of recoveredDocIds) {
    const activeDoc =
      docId === docCollection.id
        ? docCollection.doc
        : docCollection.getDoc(docId)?.spaceDoc;
    if (activeDoc) {
      docFrontend.disconnectDoc(activeDoc);
    }
  }

  for (const docId of recoveredDocIds) {
    await resetLocalDoc.call(docFrontend.storage, docId);
  }
};

const reloadWorkspaceAfterRecovery = async (
  workspaceService: WorkspaceService,
  docCollection: Workspace,
  recoveredDocIds: readonly string[],
  recoveryVersion: number
): Promise<never> => {
  await resetRecoveredLocalDocs(
    workspaceService,
    docCollection,
    recoveredDocIds
  );
  localStorage.setItem(
    RECOVERY_VERSION_KEY(docCollection.id),
    String(recoveryVersion)
  );
  window.location.reload();
  return new Promise<never>(() => {});
};

// attach the Page shown in the modal to a temporary workspace
// so that we do not need to worry about providers etc
// TODO(@Peng): fix references to the page (the referenced page will shown as deleted)
// if we simply clone the current workspace, it maybe time consuming right?
const docCollectionMap = new Map<string, Workspace>();

// assume the workspace is a cloud workspace since the history feature is only enabled for cloud workspace
const getOrCreateShellWorkspace = (
  workspaceId: string,
  flavourProvider?: WorkspaceFlavourProvider,
  scopeKey = 'default'
) => {
  const key = `${workspaceId}:${scopeKey}`;
  let docCollection = docCollectionMap.get(key);
  if (!docCollection) {
    docCollection = new WorkspaceImpl({
      id: workspaceId,
      rootDoc: new YDoc({ guid: workspaceId }),
      blobSource: {
        name: 'cloud',
        readonly: true,
        async get(key) {
          return flavourProvider?.getWorkspaceBlob(workspaceId, key) ?? null;
        },
        set() {
          return Promise.resolve('');
        },
        delete() {
          return Promise.resolve();
        },
        list() {
          return Promise.resolve([]);
        },
      },
    });
    docCollectionMap.set(key, docCollection);
    docCollection.doc.emit('sync', [true, docCollection.doc]);
  }
  return docCollection;
};

// workspace id + page id + timestamp -> snapshot (update binary)
export const usePageHistory = (
  workspaceId: string,
  pageDocId: string,
  ts?: string
) => {
  const fetchService = useService(FetchService);
  // snapshot should be immutable. so we use swr immutable to disable revalidation
  const { data } = useSWRImmutable<ArrayBuffer | null>(
    [fetchService, workspaceId, pageDocId, ts],
    {
      fetcher: snapshotFetcher,
      suspense: false,
    }
  );
  return data ?? undefined;
};

export const usePageHistoryBundle = (
  workspaceId: string,
  pageDocId: string,
  ts?: string
) => {
  const fetchService = useService(FetchService);
  const { data, isLoading } = useSWRImmutable<{
    timestamp: string;
    bundle: DecodedHistorySnapshotBundle;
  } | null>([fetchService, workspaceId, pageDocId, ts], {
    fetcher: snapshotBundleFetcher,
    suspense: false,
  });
  const loadedTimestamp = data?.timestamp;
  const bundle = ts && loadedTimestamp === ts ? data?.bundle : undefined;
  return {
    bundle,
    isLoading: !!ts && (isLoading || !bundle),
    loadedTimestamp: bundle ? loadedTimestamp : undefined,
  };
};

export const useDocHistoryCalendar = (
  workspaceId: string,
  pageDocId: string,
  start?: string,
  end?: string,
  timezone?: string
) => {
  const fetchService = useService(FetchService);
  const key =
    pageDocId && start && end && timezone
      ? [fetchService, workspaceId, pageDocId, start, end, timezone]
      : null;
  const { data, isLoading } = useSWRImmutable<DocHistoryCalendar>(key, {
    fetcher: historyCalendarFetcher,
    suspense: false,
  });
  return { calendar: data, isLoading };
};

const applySnapshotToShellDoc = (
  workspace: Workspace,
  docId: string,
  snapshot: ArrayBuffer
) => {
  let doc = workspace.getDoc(docId)?.getStore();
  if (!doc) {
    doc = workspace.createDoc(docId).getStore();
  }
  doc.readonly = false;
  doc.load(() => {
    applyUpdate(doc.spaceDoc, new Uint8Array(snapshot));
  });
  doc.readonly = true;
  return doc;
};

export const useReadonlySnapshotPageFromUpdate = (
  docCollection: Workspace,
  previewPageId?: string,
  snapshot?: ArrayBuffer,
  shellScopeKey = 'default'
) => {
  const affineWorkspace = useService(WorkspaceService).workspace;
  const workspacesService = useService(WorkspacesService);
  const page = useMemo(() => {
    if (!previewPageId) {
      return;
    }
    const historyShellWorkspace = getOrCreateShellWorkspace(
      docCollection.id,
      workspacesService.getWorkspaceFlavourProvider(affineWorkspace.meta),
      shellScopeKey
    );
    let page = historyShellWorkspace.getDoc(previewPageId)?.getStore();
    if (!page && snapshot) {
      // IMPORTANT: Apply update BEFORE setting readonly!
      // Y.js observers fire synchronously during applyUpdate.
      // If readonly is true, observers like GroupElementModel.onChange
      // will fail when trying to sync/delete elements.
      page = applySnapshotToShellDoc(
        historyShellWorkspace,
        previewPageId,
        snapshot
      );
    }
    return page ?? undefined;
  }, [
    previewPageId,
    docCollection.id,
    workspacesService,
    affineWorkspace.meta,
    snapshot,
    shellScopeKey,
  ]);

  useEffect(() => {
    const historyShellWorkspace = getOrCreateShellWorkspace(
      docCollection.id,
      workspacesService.getWorkspaceFlavourProvider(affineWorkspace.meta),
      shellScopeKey
    );
    // apply the rootdoc's update to the current workspace
    // this makes sure the page reference links are not deleted ones in the preview
    const update = encodeStateAsUpdate(docCollection.doc);
    applyUpdate(historyShellWorkspace.doc, update);
  }, [affineWorkspace.meta, docCollection, shellScopeKey, workspacesService]);

  return page;
};

export const useReadonlySnapshotPageFromBundle = (
  docCollection: Workspace,
  previewPageId?: string,
  bundle?: DecodedHistorySnapshotBundle,
  shellScopeKey = 'default'
) => {
  const affineWorkspace = useService(WorkspaceService).workspace;
  const workspacesService = useService(WorkspacesService);

  const page = useMemo(() => {
    if (!previewPageId || !bundle) {
      return;
    }
    const historyShellWorkspace = getOrCreateShellWorkspace(
      docCollection.id,
      workspacesService.getWorkspaceFlavourProvider(affineWorkspace.meta),
      shellScopeKey
    );
    for (const systemDoc of bundle.systemDocs) {
      applySnapshotToShellDoc(
        historyShellWorkspace,
        systemDoc.docId,
        systemDoc.snapshot
      );
    }
    return applySnapshotToShellDoc(
      historyShellWorkspace,
      previewPageId,
      bundle.page.snapshot
    );
  }, [
    affineWorkspace.meta,
    bundle,
    docCollection.id,
    previewPageId,
    shellScopeKey,
    workspacesService,
  ]);

  useEffect(() => {
    const historyShellWorkspace = getOrCreateShellWorkspace(
      docCollection.id,
      workspacesService.getWorkspaceFlavourProvider(affineWorkspace.meta),
      shellScopeKey
    );
    const update = encodeStateAsUpdate(docCollection.doc);
    applyUpdate(historyShellWorkspace.doc, update);
  }, [affineWorkspace.meta, docCollection, shellScopeKey, workspacesService]);

  return page;
};

export const useReadonlySnapshotPageState = (
  docCollection: Workspace,
  pageDocId: string,
  ts?: string,
  previewEnabled = true
) => {
  const previewTimestamp = previewEnabled ? ts : undefined;
  const { bundle, isLoading, loadedTimestamp } = usePageHistoryBundle(
    docCollection.id,
    pageDocId,
    previewTimestamp
  );
  const snapshotPage = useReadonlySnapshotPageFromBundle(
    docCollection,
    previewTimestamp ? pageDocId : undefined,
    bundle,
    previewTimestamp ? `history:${pageDocId}:${previewTimestamp}` : 'default'
  );
  const previewState = resolveHistoryPreviewState(
    previewTimestamp,
    loadedTimestamp,
    snapshotPage,
    isLoading
  );
  return {
    isLoading: previewState.isLoading,
    loadedTimestamp: previewState.loadedTimestamp,
    snapshotPage: previewState.snapshot,
  };
};

// workspace id + page id + timestamp + snapshot -> Page (to be used for rendering in blocksuite editor)
export const useReadonlySnapshotPage = (
  docCollection: Workspace,
  pageDocId: string,
  ts?: string,
  previewEnabled = true
) => {
  return useReadonlySnapshotPageState(
    docCollection,
    pageDocId,
    ts,
    previewEnabled
  ).snapshotPage;
};

export const useSnapshotPage = useReadonlySnapshotPage;

export const historyListGroupByDay = (histories: DocHistory[]) => {
  const map = new Map<string, DocHistory[]>();
  for (const history of histories) {
    const day = i18nTime(history.timestamp, {
      relative: {
        max: [1, 'week'],
        accuracy: 'day',
        weekday: true,
      },
      absolute: {
        accuracy: 'day',
        noYear: true,
      },
    });
    const list = map.get(day) ?? [];
    list.push(history);
    map.set(day, list);
  }
  return [...map.entries()];
};

/**
 * Reverts a document to a previous snapshot state.
 * sl-856: Added error handling for corrupt snapshot data.
 * @throws Error if the snapshot data is corrupt or revert fails
 */
export function revertUpdate(
  doc: YDoc,
  snapshotUpdate: Uint8Array,
  getMetadata: (key: string) => 'Text' | 'Map' | 'Array'
) {
  const snapshotDoc = new YDoc();

  // sl-856: Apply snapshot with error handling
  try {
    applyUpdate(snapshotDoc, snapshotUpdate);
  } catch (err) {
    console.error(
      '[revertUpdate] Failed to apply snapshot (corrupt data?):',
      err
    );
    throw new Error(
      'Cannot revert: the history snapshot appears to be corrupted'
    );
  }

  const currentStateVector = encodeStateVector(doc);
  const snapshotStateVector = encodeStateVector(snapshotDoc);

  const changesSinceSnapshotUpdate = encodeStateAsUpdate(
    doc,
    snapshotStateVector
  );
  const undoManager = new UndoManager(
    // oxlint-disable array-callback-return
    [...snapshotDoc.share.keys()].map(key => {
      const type = getMetadata(key);
      if (type === 'Text') {
        return snapshotDoc.getText(key);
      } else if (type === 'Map') {
        return snapshotDoc.getMap(key);
      } else if (type === 'Array') {
        return snapshotDoc.getArray(key);
      }

      throw new Error('Unknown type');
    })
  );

  // sl-856: Apply changes for undo calculation with error handling
  try {
    applyUpdate(snapshotDoc, changesSinceSnapshotUpdate);
  } catch (err) {
    console.error('[revertUpdate] Failed to apply changes for undo:', err);
    throw new Error('Cannot revert: failed to calculate undo changes');
  }

  undoManager.undo();
  const revertChangesSinceSnapshotUpdate = encodeStateAsUpdate(
    snapshotDoc,
    currentStateVector
  );

  // sl-856: Apply revert to actual document with error handling
  try {
    applyUpdate(doc, revertChangesSinceSnapshotUpdate);
  } catch (err) {
    console.error('[revertUpdate] Failed to apply revert to document:', err);
    throw new Error(
      'Failed to revert document - please try again or contact support'
    );
  }
}

interface RestorePageOptions {
  downloadCurrentStateSnapshot?: boolean;
}

export const useRestorePage = (docCollection: Workspace, pageDocId: string) => {
  const mutateQueryResource = useMutateQueryResource();
  const fetchService = useService(FetchService);
  const workspaceService = useService(WorkspaceService);
  const [isMutating, setIsMutating] = useState(false);

  const onRestore = useCallback(
    async (version: string, options: RestorePageOptions = {}) => {
      if (!pageDocId || isMutating) {
        return;
      }

      setIsMutating(true);
      try {
        const candidate = await findRecoveryCandidateForTimestamp(
          fetchService,
          docCollection.id,
          pageDocId,
          version
        );
        let result: RecoveryRunResult;
        try {
          result = await restoreRecoveryCandidate(
            fetchService,
            docCollection.id,
            pageDocId,
            candidate
          );
        } catch (error) {
          if (
            getRecoveryApiErrorCode(error) !==
            RECOVERY_CANDIDATE_FINGERPRINT_MISMATCH_CODE
          ) {
            throw error;
          }

          logger.warn(
            'Recovery candidate fingerprint changed; retrying with fresh candidate',
            pageDocId,
            version
          );
          const refreshedCandidate = await findRecoveryCandidateForTimestamp(
            fetchService,
            docCollection.id,
            pageDocId,
            version
          );
          result = await restoreRecoveryCandidate(
            fetchService,
            docCollection.id,
            pageDocId,
            refreshedCandidate
          );
        }
        if (options.downloadCurrentStateSnapshot !== false) {
          downloadCurrentStateSnapshot(result);
        }

        await mutateQueryResource(listHistoryQuery, vars => {
          return (
            vars.pageDocId === pageDocId &&
            vars.workspaceId === docCollection.id
          );
        });

        logger.info('Page restored', pageDocId, version);
        await reloadWorkspaceAfterRecovery(
          workspaceService,
          docCollection,
          result.recoveredDocIds,
          result.recoveryVersion
        );
      } finally {
        setIsMutating(false);
      }
    },
    [
      docCollection,
      fetchService,
      isMutating,
      mutateQueryResource,
      pageDocId,
      workspaceService,
    ]
  );

  return {
    onRestore,
    isMutating,
  };
};
